import { MAIL_TEMPLATE_KEYS } from './mail-templates';
import { mailCtaButtonHtml } from './mail-layout-brand';

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export type MailTemplateContent = {
  subject: string;
  htmlBody: string;
  textBody: string;
};

function greeting(name = 'there') {
  return `Hi ${escapeHtml(name)},`;
}

/** Inner HTML bodies (before layout wrap) for DB seeding and code fallback. */
export function getDefaultTemplateContent(
  key: string,
  variables: Record<string, unknown> = {},
): MailTemplateContent | null {
  const displayName = String(variables.displayName ?? variables.firstName ?? 'there');
  const email = variables.email ? String(variables.email) : undefined;

  switch (key) {
    case MAIL_TEMPLATE_KEYS.AUTH_VERIFY_EMAIL: {
      const code = String(variables.code ?? '000000');
      return {
        subject: 'Verify your Loamify email',
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Thanks for joining Loamify. Enter this verification code to confirm your email address${email ? ` (<strong>${escapeHtml(email)}</strong>)` : ''}:</p>
<p style="margin:20px 0;font-size:28px;font-weight:700;letter-spacing:0.25em;color:#1a1d21;">${escapeHtml(code)}</p>
<p style="color:#666;">This code expires in 10 minutes. If you did not create a Loamify account, you can ignore this message.</p>`,
        textBody: `Hi ${displayName},\n\nYour Loamify verification code is ${code}. It expires in 10 minutes.`,
      };
    }
    case MAIL_TEMPLATE_KEYS.AUTH_PASSWORD_RESET: {
      const code = String(variables.code ?? '000000');
      return {
        subject: 'Reset your Loamify password',
        htmlBody: `<p>${greeting(displayName)}</p>
<p>We received a request to reset the password for your Loamify account. Use the code below to choose a new password:</p>
<p style="margin:20px 0;font-size:28px;font-weight:700;letter-spacing:0.25em;color:#1a1d21;">${escapeHtml(code)}</p>
<p style="color:#666;">This code expires in 10 minutes. If you did not request a reset, no action is needed — your password will stay the same.</p>`,
        textBody: `Hi ${displayName},\n\nYour Loamify password reset code is ${code}. It expires in 10 minutes.`,
      };
    }
    case MAIL_TEMPLATE_KEYS.AUTH_EMAIL_CHANGE: {
      const newEmail = String(variables.newEmail ?? 'you@example.com');
      const href = String(variables.href ?? '#');
      return {
        subject: 'Confirm your new email address',
        htmlBody: `<p>${greeting(displayName)}</p>
<p>You requested to change your Loamify sign-in email to <strong>${escapeHtml(newEmail)}</strong>.</p>
<p>Confirm this change to keep your account secure. The link below expires after a limited time.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Confirm email change', href)}</p>`,
        textBody: `Hi ${displayName},\n\nConfirm your new email ${newEmail}: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.AUTH_SECURITY_ALERT: {
      const action = String(variables.action ?? 'A security change occurred');
      const href = String(variables.href ?? '#');
      return {
        subject: 'Security alert on your Loamify account',
        htmlBody: `<p>${greeting(displayName)}</p>
<p><strong>${escapeHtml(action)}</strong> on your Loamify account.</p>
<p>If this was you, no further action is needed. If you do not recognize this activity, review your active sessions and update your password immediately.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Review account security', href)}</p>`,
        textBody: `Hi ${displayName},\n\nSecurity alert: ${action}.\nReview settings: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.AUTH_MEMBER_WELCOME: {
      const feedHref = String(variables.feedHref ?? '#');
      const settingsHref = String(variables.settingsHref ?? '#');
      return {
        subject: 'Welcome to Loamify',
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Your email is verified — welcome to the community. Explore your feed, follow creators you care about, and make your profile yours.</p>
<p style="margin:8px 0 0;">Here are a few ways to get started:</p>
<ul style="padding-left:20px;margin:12px 0;line-height:1.7;">
  <li>Complete your profile and add a photo</li>
  <li>Follow topics that match your interests</li>
  <li>Join Circles to learn alongside others</li>
</ul>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Open your feed', feedHref)}</p>
<p style="margin:16px 0 0;font-size:13px;color:#888;">Manage notifications anytime in <a href="${escapeHtml(settingsHref)}" style="color:#1a1d21;">settings</a>.</p>`,
        textBody: `Welcome to Loamify, ${displayName}! Open your feed: ${feedHref}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.AUTH_CREATOR_WELCOME: {
      const studioHref = String(variables.studioHref ?? '#');
      const profileHref = String(variables.profileHref ?? '#');
      return {
        subject: 'Welcome to Loamify Creator',
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Your creator account is active. Loamify Studio is where you run your business — publish content, grow your audience, and manage Circles, courses, and more.</p>
<p>Your public profile is live. Share it with your audience so they can follow you on Loamify.</p>
<p style="margin:24px 0 8px;">${mailCtaButtonHtml('Open Loamify Studio', studioHref)}</p>
<p style="font-size:13px;"><a href="${escapeHtml(profileHref)}" style="color:#1a1d21;">View your public profile →</a></p>`,
        textBody: `Welcome to Loamify Creator, ${displayName}! Studio: ${studioHref} Profile: ${profileHref}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.BILLING_RECEIPT: {
      const planName = String(variables.planName ?? 'Creator Plan');
      const amountLabel = String(variables.amountLabel ?? '');
      const billingInterval = String(variables.billingInterval ?? '');
      const invoiceNumber = variables.invoiceNumber ? String(variables.invoiceNumber) : null;
      const href = String(variables.href ?? '#');
      const detailLines = [
        amountLabel ? `<strong>Amount paid:</strong> ${escapeHtml(amountLabel)}` : null,
        billingInterval ? `<strong>Billing:</strong> ${escapeHtml(billingInterval)}` : null,
        invoiceNumber ? `<strong>Invoice:</strong> ${escapeHtml(invoiceNumber)}` : null,
      ].filter(Boolean);
      return {
        subject: `Payment received — ${planName}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Thank you for your payment. This email confirms your recent Loamify subscription charge.</p>
${
  detailLines.length
    ? `<table role="presentation" style="margin:16px 0;width:100%;border-collapse:collapse;">
  ${detailLines
    .map(
      (line) =>
        `<tr><td style="padding:8px 0;border-bottom:1px solid #eee;font-size:14px;">${line}</td></tr>`,
    )
    .join('')}
</table>`
    : ''
}
<p style="margin:16px 0 0;">${mailCtaButtonHtml('View billing details', href)}</p>`,
        textBody: `Hi ${displayName},\n\nPayment received for ${planName}.${amountLabel ? `\nAmount: ${amountLabel}` : ''}${invoiceNumber ? `\nInvoice: ${invoiceNumber}` : ''}\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_MENTION_MISSED: {
      const actorName = String(variables.actorName ?? 'Someone');
      const href = String(variables.href ?? '#');
      return {
        subject: `${actorName} mentioned you on Loamify`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p><strong>${escapeHtml(actorName)}</strong> mentioned you in a post while you were away. Jump back in to join the conversation.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('View mention', href)}</p>`,
        textBody: `Hi ${displayName},\n\n${actorName} mentioned you. View: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_DM_MISSED: {
      const senderName = String(variables.senderName ?? 'Someone');
      const preview = String(variables.preview ?? 'New message');
      const href = String(variables.href ?? '#');
      return {
        subject: `New message from ${senderName}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p><strong>${escapeHtml(senderName)}</strong> sent you a direct message:</p>
<p style="padding:14px 16px;background:#f4f4f5;border-radius:12px;font-size:14px;line-height:1.5;color:#333;">${escapeHtml(preview)}</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Open conversation', href)}</p>`,
        textBody: `Hi ${displayName},\n\n${senderName}: ${preview}\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_DAILY_DIGEST:
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST: {
      const lines = String(variables.digestLines ?? '').split('\n').filter(Boolean);
      const isWeekly = key === MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST;
      const href = String(variables.notificationsHref ?? '#');
      return {
        subject: isWeekly ? 'Your Loamify weekly digest' : 'Your Loamify daily digest',
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Here's ${isWeekly ? 'what you missed this week' : "what happened while you were away"} on Loamify:</p>
${lines.map((line) => `<p style="margin:0 0 10px;padding-left:12px;border-left:3px solid #ff8025;">${escapeHtml(line)}</p>`).join('')}
<p style="margin:24px 0 0;">${mailCtaButtonHtml('See all notifications', href)}</p>`,
        textBody: `Hi ${displayName},\n\n${lines.join('\n')}\n\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_IMMEDIATE: {
      const summary = String(variables.summary ?? 'New activity on Loamify');
      const href = String(variables.href ?? '#');
      return {
        subject: summary,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>${escapeHtml(summary)}</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('View on Loamify', href)}</p>`,
        textBody: `Hi ${displayName},\n\n${summary}\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.PLATFORM_ANNOUNCEMENT: {
      const title = String(variables.title ?? 'Update from Loamify');
      const body = String(variables.body ?? '');
      const ctaLabel = variables.ctaLabel ? String(variables.ctaLabel) : undefined;
      const ctaHref = variables.ctaHref ? String(variables.ctaHref) : undefined;
      const ctaHtml =
        ctaLabel && ctaHref ? `<p style="margin:24px 0 0;">${mailCtaButtonHtml(ctaLabel, ctaHref)}</p>` : '';
      return {
        subject: title,
        htmlBody: `<p>${greeting(displayName)}</p>
<p style="font-size:16px;font-weight:600;margin:0 0 12px;">${escapeHtml(title)}</p>
<p>${escapeHtml(body).replace(/\n/g, '<br/>')}</p>
${ctaHtml}`,
        textBody: `Hi ${displayName},\n\n${title}\n\n${body}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.COMMUNITY_BROADCAST: {
      const communityName = String(variables.communityName ?? 'Community');
      const title = String(variables.title ?? 'Community update');
      const body = String(variables.body ?? '');
      const ctaLabel = variables.ctaLabel ? String(variables.ctaLabel) : undefined;
      const ctaHref = variables.ctaHref ? String(variables.ctaHref) : undefined;
      const discussionCtaLabel = variables.discussionCtaLabel
        ? String(variables.discussionCtaLabel)
        : undefined;
      const discussionCtaHref = variables.discussionCtaHref
        ? String(variables.discussionCtaHref)
        : undefined;
      const ctaHtml =
        ctaLabel && ctaHref ? `<p style="margin:24px 0 0;">${mailCtaButtonHtml(ctaLabel, ctaHref)}</p>` : '';
      const discussionHtml =
        discussionCtaLabel && discussionCtaHref
          ? `<p style="margin:16px 0 0;font-size:14px;"><a href="${escapeHtml(discussionCtaHref)}" style="color:#555;text-decoration:underline;">${escapeHtml(discussionCtaLabel)}</a></p>`
          : '';
      return {
        subject: title,
        htmlBody: `<p>${greeting(displayName)}</p>
<p style="font-size:12px;color:#888;margin:0 0 8px;text-transform:uppercase;letter-spacing:0.06em;">${escapeHtml(communityName)}</p>
<p style="font-size:16px;font-weight:600;margin:0 0 12px;">${escapeHtml(title)}</p>
<p>${escapeHtml(body).replace(/\n/g, '<br/>')}</p>
${ctaHtml}${discussionHtml}`,
        textBody: `${communityName}\n\nHi ${displayName},\n\n${title}\n\n${body}${discussionCtaHref ? `\n\n${discussionCtaLabel ?? 'Discuss'}: ${discussionCtaHref}` : ''}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.COURSE_PURCHASE_RECEIPT: {
      const courseTitle = String(variables.courseTitle ?? 'Course');
      const amountLabel = variables.amountLabel ? String(variables.amountLabel) : '';
      const href = String(variables.receiptUrl ?? variables.href ?? '#');
      return {
        subject: `Receipt: ${courseTitle}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Thank you for your purchase. This email confirms payment for <strong>${escapeHtml(courseTitle)}</strong>.</p>
${amountLabel ? `<p><strong>Amount paid:</strong> ${escapeHtml(amountLabel)}</p>` : ''}
<p style="margin:24px 0 0;">${mailCtaButtonHtml('View course', href)}</p>`,
        textBody: `Hi ${displayName},\n\nReceipt for ${courseTitle}.${amountLabel ? `\nAmount: ${amountLabel}` : ''}\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.COURSE_ENROLLED: {
      const courseTitle = String(variables.courseTitle ?? 'Course');
      const href = String(variables.href ?? '#');
      return {
        subject: `You're enrolled in ${courseTitle}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>You're all set — welcome to <strong>${escapeHtml(courseTitle)}</strong>. Start learning at your own pace whenever you're ready.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Start learning', href)}</p>`,
        textBody: `Hi ${displayName},\n\nYou're enrolled in ${courseTitle}.\nStart learning: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.COURSE_DRIP_UNLOCK: {
      const courseTitle = String(variables.courseTitle ?? 'Course');
      const moduleTitle = String(variables.moduleTitle ?? 'New module');
      const href = String(variables.href ?? '#');
      return {
        subject: `New lessons unlocked: ${moduleTitle}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>New content is ready in <strong>${escapeHtml(courseTitle)}</strong>: <strong>${escapeHtml(moduleTitle)}</strong> has unlocked.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Continue learning', href)}</p>`,
        textBody: `Hi ${displayName},\n\n${moduleTitle} unlocked in ${courseTitle}.\nContinue: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.COURSE_CERTIFICATE_READY: {
      const courseTitle = String(variables.courseTitle ?? 'Course');
      const certificateCode = variables.certificateCode ? String(variables.certificateCode) : null;
      const href = String(variables.href ?? '#');
      return {
        subject: `Your certificate for ${courseTitle} is ready`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Congratulations — you completed <strong>${escapeHtml(courseTitle)}</strong>. Your certificate is ready to download.</p>
${certificateCode ? `<p style="font-size:13px;color:#666;">Certificate ID: ${escapeHtml(certificateCode)}</p>` : ''}
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Download certificate', href)}</p>`,
        textBody: `Hi ${displayName},\n\nYour certificate for ${courseTitle} is ready.${certificateCode ? `\nID: ${certificateCode}` : ''}\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_PERK_DELIVERED: {
      const membershipTitle = String(variables.membershipTitle ?? 'Membership');
      const perkTitle = String(variables.perkTitle ?? 'Perk');
      const accessUrl = String(variables.accessUrl ?? variables.href ?? '#');
      const href = String(variables.href ?? accessUrl);
      return {
        subject: `Your access: ${perkTitle}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Your perk from <strong>${escapeHtml(membershipTitle)}</strong> is ready.</p>
<p style="padding:14px 16px;background:#f4f4f5;border-radius:12px;font-size:14px;"><strong>${escapeHtml(perkTitle)}</strong></p>
<p style="margin:16px 0 0;">${mailCtaButtonHtml('Open access link', accessUrl)}</p>
<p style="margin:16px 0 0;font-size:13px;"><a href="${escapeHtml(href)}" style="color:#1a1d21;">View membership →</a></p>`,
        textBody: `Hi ${displayName},\n\nYour perk "${perkTitle}" from ${membershipTitle} is ready.\nAccess: ${accessUrl}\nMembership: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_SUBSCRIPTION_RECEIPT: {
      const membershipTitle = String(variables.membershipTitle ?? 'Membership');
      const tierName = String(variables.tierName ?? 'Plan');
      const amountLabel = variables.amountLabel ? String(variables.amountLabel) : '';
      const href = String(variables.href ?? '#');
      return {
        subject: `Receipt: ${membershipTitle}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Thanks for subscribing to <strong>${escapeHtml(membershipTitle)}</strong>.</p>
<p><strong>Plan:</strong> ${escapeHtml(tierName)}${amountLabel ? `<br/><strong>Amount:</strong> ${escapeHtml(amountLabel)}` : ''}</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('View membership', href)}</p>`,
        textBody: `Hi ${displayName},\n\nThanks for subscribing to ${membershipTitle}.\nPlan: ${tierName}${amountLabel ? `\nAmount: ${amountLabel}` : ''}\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_WELCOME: {
      const membershipTitle = String(variables.membershipTitle ?? 'Membership');
      const tierName = String(variables.tierName ?? 'Plan');
      const creatorName = variables.creatorName ? String(variables.creatorName) : null;
      const href = String(variables.href ?? '#');
      return {
        subject: `Welcome to ${membershipTitle}`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>You're in — welcome to <strong>${escapeHtml(membershipTitle)}</strong>${creatorName ? ` by ${escapeHtml(creatorName)}` : ''}.</p>
<p>Your tier: <strong>${escapeHtml(tierName)}</strong>. Perks and access links will arrive separately if your plan includes them.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Open membership', href)}</p>`,
        textBody: `Hi ${displayName},\n\nWelcome to ${membershipTitle} (${tierName}).\n${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_BILLING_PAST_DUE: {
      const membershipTitle = String(variables.membershipTitle ?? 'Membership');
      const tierName = String(variables.tierName ?? 'Plan');
      const href = String(variables.href ?? '#');
      return {
        subject: `Action required: ${membershipTitle} payment`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>We couldn't process your latest payment for <strong>${escapeHtml(membershipTitle)}</strong> (${escapeHtml(tierName)}).</p>
<p>Update your billing details to keep your access — we'll retry automatically once payment succeeds.</p>
<p style="margin:24px 0 0;">${mailCtaButtonHtml('Update payment method', href)}</p>`,
        textBody: `Hi ${displayName},\n\nPayment failed for ${membershipTitle} (${tierName}). Update billing: ${href}`,
      };
    }
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_CANCELLED: {
      const membershipTitle = String(variables.membershipTitle ?? 'Membership');
      const tierName = variables.tierName ? String(variables.tierName) : null;
      const href = variables.href ? String(variables.href) : undefined;
      const ctaHtml = href
        ? `<p style="margin:24px 0 0;">${mailCtaButtonHtml('Browse membership', href)}</p>`
        : '';
      return {
        subject: `${membershipTitle} access ended`,
        htmlBody: `<p>${greeting(displayName)}</p>
<p>Your access to <strong>${escapeHtml(membershipTitle)}</strong>${tierName ? ` (${escapeHtml(tierName)})` : ''} has ended.</p>
<p>If this was unexpected, check your billing settings or contact the creator for help.</p>
${ctaHtml}`,
        textBody: `Hi ${displayName},\n\nYour access to ${membershipTitle} has ended.${href ? `\n${href}` : ''}`,
      };
    }
    default:
      return null;
  }
}

export function getDefaultLayoutTitle(key: string): string {
  switch (key) {
    case MAIL_TEMPLATE_KEYS.AUTH_VERIFY_EMAIL:
      return 'Verify your email';
    case MAIL_TEMPLATE_KEYS.AUTH_PASSWORD_RESET:
      return 'Reset your password';
    case MAIL_TEMPLATE_KEYS.AUTH_EMAIL_CHANGE:
      return 'Confirm email change';
    case MAIL_TEMPLATE_KEYS.AUTH_SECURITY_ALERT:
      return 'Security alert';
    case MAIL_TEMPLATE_KEYS.AUTH_MEMBER_WELCOME:
      return 'Welcome to Loamify';
    case MAIL_TEMPLATE_KEYS.AUTH_CREATOR_WELCOME:
      return 'Welcome, Creator';
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_MENTION_MISSED:
      return 'You were mentioned';
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_DM_MISSED:
      return 'New direct message';
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_DAILY_DIGEST:
      return 'Your daily digest';
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST:
      return 'Your weekly digest';
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_IMMEDIATE:
      return 'New notification';
    case MAIL_TEMPLATE_KEYS.PLATFORM_ANNOUNCEMENT:
      return 'News from Loamify';
    case MAIL_TEMPLATE_KEYS.COMMUNITY_BROADCAST:
      return 'Community update';
    case MAIL_TEMPLATE_KEYS.BILLING_RECEIPT:
      return 'Payment receipt';
    case MAIL_TEMPLATE_KEYS.COURSE_PURCHASE_RECEIPT:
      return 'Course purchase receipt';
    case MAIL_TEMPLATE_KEYS.COURSE_ENROLLED:
      return 'Course enrollment';
    case MAIL_TEMPLATE_KEYS.COURSE_DRIP_UNLOCK:
      return 'New lessons unlocked';
    case MAIL_TEMPLATE_KEYS.COURSE_CERTIFICATE_READY:
      return 'Certificate ready';
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_PERK_DELIVERED:
      return 'Membership perk';
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_SUBSCRIPTION_RECEIPT:
      return 'Membership receipt';
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_WELCOME:
      return 'Welcome to membership';
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_BILLING_PAST_DUE:
      return 'Payment action required';
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_CANCELLED:
      return 'Membership ended';
    default:
      return 'Loamify';
  }
}

export function getDefaultCta(key: string, variables: Record<string, unknown>) {
  const href = variables.href ?? variables.notificationsHref ?? variables.ctaHref;
  if (typeof href !== 'string' || !href) return undefined;

  switch (key) {
    case MAIL_TEMPLATE_KEYS.AUTH_EMAIL_CHANGE:
      return { label: 'Confirm email change', href };
    case MAIL_TEMPLATE_KEYS.AUTH_SECURITY_ALERT:
      return { label: 'Review account security', href };
    case MAIL_TEMPLATE_KEYS.AUTH_MEMBER_WELCOME:
      return { label: 'Open your feed', href: String(variables.feedHref ?? href) };
    case MAIL_TEMPLATE_KEYS.AUTH_CREATOR_WELCOME:
      return { label: 'Open Loamify Studio', href: String(variables.studioHref ?? href) };
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_MENTION_MISSED:
      return { label: 'View mention', href };
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_DM_MISSED:
      return { label: 'Open conversation', href };
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_DAILY_DIGEST:
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST:
      return { label: 'See all notifications', href };
    case MAIL_TEMPLATE_KEYS.NOTIFICATION_IMMEDIATE:
      return { label: 'View on Loamify', href };
    case MAIL_TEMPLATE_KEYS.PLATFORM_ANNOUNCEMENT:
      return variables.ctaLabel
        ? { label: String(variables.ctaLabel), href }
        : undefined;
    case MAIL_TEMPLATE_KEYS.COMMUNITY_BROADCAST:
      return variables.ctaHref
        ? { label: String(variables.ctaLabel ?? 'Open community'), href: String(variables.ctaHref) }
        : undefined;
    case MAIL_TEMPLATE_KEYS.BILLING_RECEIPT:
      return { label: 'View billing details', href: String(variables.href ?? href) };
    case MAIL_TEMPLATE_KEYS.COURSE_PURCHASE_RECEIPT:
      return { label: 'View course', href: String(variables.receiptUrl ?? variables.href ?? href) };
    case MAIL_TEMPLATE_KEYS.COURSE_ENROLLED:
      return { label: 'Start learning', href };
    case MAIL_TEMPLATE_KEYS.COURSE_DRIP_UNLOCK:
      return { label: 'Continue learning', href };
    case MAIL_TEMPLATE_KEYS.COURSE_CERTIFICATE_READY:
      return { label: 'Download certificate', href };
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_PERK_DELIVERED:
      return {
        label: 'Open access link',
        href: String(variables.accessUrl ?? variables.href ?? href),
      };
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_SUBSCRIPTION_RECEIPT:
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_WELCOME:
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_BILLING_PAST_DUE:
      return { label: 'View membership', href: String(variables.href ?? href) };
    case MAIL_TEMPLATE_KEYS.MEMBERSHIP_CANCELLED:
      return href ? { label: 'Browse membership', href: String(href) } : undefined;
    default:
      return undefined;
  }
}

/** Seed-friendly inner HTML with {{variable}} tokens preserved in editable prose. */
export function getSeedTemplateContent(key: string): MailTemplateContent | null {
  const tokenVars: Record<string, unknown> = {
    displayName: '{{displayName}}',
    firstName: '{{firstName}}',
    email: '{{email}}',
    code: '{{code}}',
    newEmail: '{{newEmail}}',
    href: '{{href}}',
    action: '{{action}}',
    feedHref: '{{feedHref}}',
    settingsHref: '{{settingsHref}}',
    studioHref: '{{studioHref}}',
    profileHref: '{{profileHref}}',
    actorName: '{{actorName}}',
    senderName: '{{senderName}}',
    preview: '{{preview}}',
    digestLines: '{{digestLines}}',
    notificationsHref: '{{notificationsHref}}',
    summary: '{{summary}}',
    title: '{{title}}',
    body: '{{body}}',
    communityName: '{{communityName}}',
    planName: '{{planName}}',
    amountLabel: '{{amountLabel}}',
    billingInterval: '{{billingInterval}}',
    invoiceNumber: '{{invoiceNumber}}',
    courseTitle: '{{courseTitle}}',
    moduleTitle: '{{moduleTitle}}',
    certificateCode: '{{certificateCode}}',
    receiptUrl: '{{receiptUrl}}',
    membershipTitle: '{{membershipTitle}}',
    tierName: '{{tierName}}',
    perkTitle: '{{perkTitle}}',
    accessUrl: '{{accessUrl}}',
    creatorName: '{{creatorName}}',
  };
  return getDefaultTemplateContent(key, tokenVars);
}
